import { useState, useMemo, useEffect } from 'react';
import { useDebounce } from './useDebounce';

export const useTableOperations = (items, defaultSortField = 'name') => {
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const debouncedSearch = useDebounce(search, 300);

  const handleSort = (field) => {
    const direction = (sortField === field && sortDirection === 'asc') ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
  };

  const filteredAndSortedItems = useMemo(() => {
    return items
      .filter(item => {
        const searchLower = search.toLowerCase();
        const matchesSearch = !searchLower || Object.values(item).some(val =>
          String(val || '').toLowerCase().includes(searchLower)
        );
        const matchesStatus = statusFilter === '' || 
          (statusFilter === 'active' ? item.exIsActive : !item.exIsActive);
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        const aVal = a[sortField] || '';
        const bVal = b[sortField] || '';
        if (typeof aVal === 'string') {
          return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      });
  }, [items, search, statusFilter, sortField, sortDirection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  return {
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    sortField,
    sortDirection,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    handleSort,
    filteredAndSortedItems,
    paginatedItems,
    totalPages,
    startIndex
  };
};
